import '../../../utils/ArrayExtensions';
import { ObjectIcons } from "../../../components/icons";
import { ItemInitializer, ModInitializer } from "./ItemInitializers";
import { ModSlot, IsLegalModForSlot as IsLegalModForModSlot, SlotTypesMatch } from "./mods/ModSlot";
import { CharacterAbility } from '../../abilities/Ability';
import { OwnedItemReference, OwnedModReference } from '../../Ownership';

export type ItemSortableProperty = 'Name' | 'Cost' | 'Weight' | 'Tags';

const getName = (item: ItemInitializer, reference?: OwnedItemReference) => {
    return reference?.customName ?? item.name;
}

const deepCopy = (reference: OwnedItemReference): OwnedItemReference => ({
    ...reference,
    customSlots: reference.customSlots.map(deepCopySlot),
});

const deepCopySlot = (modSlot: ModSlot): ModSlot => ({
    ...modSlot,
    slotTypes: [...modSlot.slotTypes],
});

const getTotalHandsUsed = (allItems: ItemInitializer[], reference: OwnedItemReference, characterMods: OwnedModReference[]): number => {
    const item = allItems.find(i => i.id == reference.id);
    if (item == null) {
        console.error(`Requested hands used for nonexistent item ${reference.id}.`);
        return 0;
    }
    const modHandsUsed = characterMods
        .filter(m => m.assignedItemId == reference.instanceId)
        .sum(m => getTotalHandsUsed(allItems, m, characterMods));
    return item.handsUsedModifier + modHandsUsed;
}

const getIcon = (item: ItemInitializer) => ObjectIcons.GetIcon(item.icon);

const getTagText = (item: ItemInitializer) => item.tags.join(', ');

const isLegalItemForMod = (item: ItemInitializer, slot: ModSlot, mod: ModInitializer) => {
    //If slot types exactly match, override any other restrictions.
    if (SlotTypesMatch(slot, mod)) {
        return true;
    }
    return mod.assignableToTags.length == 0
        || item.tags.intersection(mod.assignableToTags).length > 0;
}

const isLegalModForSlot = (item: ItemInitializer, reference: OwnedItemReference, mod: ModInitializer, slotId: string) => {
    const slot = item.slots.find(s => s.id == slotId)
        ?? reference.customSlots.find(s => s.id == slotId);
    if (slot == null) {
        return false;
    }
    return IsLegalModForModSlot(slot, mod)
        && isLegalItemForMod(item, slot, mod)
        && !item.blacklistTags.intersection(mod.tags).any();
}

const filterByName = (item: ItemInitializer, check: string) => {
    return item.name.toLowerCase()
        .indexOf(check.toLowerCase()) > -1;
}

const getBaseActions = (item: ItemInitializer) => item.actions.map(a => ({
    ...a,
    source: item.name,
}));

const getActions = (allItems: ItemInitializer[], reference: OwnedItemReference, characterMods: OwnedModReference[]): CharacterAbility[] => {
    const item = allItems.find(i => i.id == reference.id);
    if (item == null) {
        console.error(`Requested actions for nonexistent item ${reference.id}.`);
        return [];
    }

    const itemActions: CharacterAbility[] = item.actions.map(a => ({
        ...a,
        source: getName(item, reference),
    }));
    const modActions: CharacterAbility[] = characterMods
        .filter(m => m.assignedItemId == reference.instanceId)
        .mapMany(m => getActions(allItems, m, characterMods));
    return [...itemActions, ...modActions];
}

const getBaseReactions = (item: ItemInitializer) => item.reactions.map(r => ({
    ...r,
    source: item.name
}));

const getReactions = (allItems: ItemInitializer[], reference: OwnedItemReference, characterMods: OwnedModReference[]): CharacterAbility[] => {
    const item = allItems.find(i => i.id == reference.id);
    if (item == null) {
        console.error(`Requested reactions for nonexistent item ${reference.id}.`);
        return [];
    }

    const itemReactions: CharacterAbility[] = item.reactions.map(r => ({
        ...r,
        source: getName(item, reference)
    }));
    const modReactions: CharacterAbility[] = characterMods
        .filter(m => m.assignedItemId == reference.instanceId)
        .mapMany(m => getReactions(allItems, m, characterMods));
    return [...itemReactions, ...modReactions];
}

const getBasePassives = (item: ItemInitializer) => item.passives.map(p => ({
    ...p,
    juices: false,
    dejuices: false,
    source: item.name
}));

const getPassives = (allItems: ItemInitializer[], reference: OwnedItemReference, characterMods: OwnedModReference[]): CharacterAbility[] => {
    const item = allItems.find(i => i.id == reference.id);
    if (item == null) {
        console.error(`Requested passives for nonexistent item ${reference.id}.`);
        return [];
    }

    const itemPassives: CharacterAbility[] = item.passives.map(p => ({
        ...p,
        juices: false,
        dejuices: false,
        source: getName(item, reference)
    }));
    const modPassives: CharacterAbility[] = characterMods
        .filter(m => m.assignedItemId == reference.instanceId)
        .mapMany(m => getPassives(allItems, m, characterMods))
    return [...itemPassives, ...modPassives].unique(ca => ca.id);
}

const isMod = (reference: OwnedItemReference) => {
    return reference.type == 'Mod'
        || reference.type == 'Ammo'
        || reference.type == 'Spell';
}

export const ItemHelpers = Object.freeze({
    getName,
    deepCopy,
    getTotalHandsUsed,
    getIcon,
    getTagText,
    isLegalModForSlot,
    filterByName,
    getBaseActions,
    getActions,
    getBaseReactions,
    getReactions,
    getBasePassives,
    getPassives,
    isMod
})